import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScriptUpload } from './ScriptUpload';
import type { StartAnalysisOptions } from './ScriptUpload';
import { AnalysisProgress } from './AnalysisProgress';
import { AnalysisResults } from './AnalysisResults';
import { useStartAnalysis, useCancelAnalysis } from '@/hooks/useScriptAnalysis';
import { useAnalysisProgress } from '@/hooks/useAnalysisProgress';
import { useOrganization } from '@/hooks/useOrganization';
import { supabase } from '@/integrations/supabase/client';

type PageView = 'empty' | 'upload' | 'progress' | 'results' | 'error';

export default function ScriptAnalysisPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlJobId = searchParams.get('job');
  // V3 · si venimos de "Subir nueva versión", este análisis es una versión del job padre.
  const parentJobId = searchParams.get('parent_job_id');

  const [view, setView] = useState<PageView>(urlJobId ? 'results' : parentJobId ? 'upload' : 'empty');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(urlJobId);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [lastOpts, setLastOpts] = useState<StartAnalysisOptions>({ formato: 'largo', tipo: 'ficcion', sourceDocType: 'guion' });

  const startMutation = useStartAnalysis();
  const cancelMutation = useCancelAnalysis();
  const job = useAnalysisProgress(jobId);
  const { data: org } = useOrganization(jobId);

  useEffect(() => {
    if (!job || view !== 'progress') return;
    if (job.status === 'completed') {
      setView('results');
      // Persist job ID in URL so it survives navigation
      if (jobId) {
        navigate(`/?job=${jobId}`, { replace: true });
      }
    } else if (job.status === 'failed') {
      setErrorMessage(job.error_message || 'Error desconocido en el analisis');
      setView('error');
    } else if (job.status === 'cancelled') {
      setView('upload');
      setJobId(null);
      toast.info('Analisis cancelado');
    }
  }, [job?.status, view]);

  async function handleStartAnalysis(opts: StartAnalysisOptions) {
    if (!selectedFile) return;
    setLastOpts(opts);
    // V3 · una NUEVA VERSIÓN no consume plaza de plan (el trigger cuenta project_id
    // distintos) y es esperado que reuse el mismo nombre de archivo → saltamos los dos
    // guards (límite + "mismo archivo"), que si no bloquearían la subida de la versión.
    // Tope de análisis de la cuenta: pre-chequeo amable antes de arrancar (el trigger
    // en la base es el backstop duro). max_analyses NULL = ilimitado → se saltea.
    if (!parentJobId && org?.max_analyses != null) {
      // El límite es de PROYECTOS ACTIVOS, así que hay que contar `project_id` DISTINTOS
      // y excluir los archivados, exactamente igual que el trigger enforce_analysis_limit
      // (`count(distinct aj.project_id) ... and aj.archived_at is null`).
      // Antes se contaban FILAS de analysis_jobs y sin filtrar archivados, así que varias
      // versiones del mismo proyecto (o proyectos ya archivados) gastaban plaza y el
      // pre-chequeo bloqueaba subidas que la base de datos SÍ permitía. Caso real
      // (4-ago-2026): una cuenta Productora con 7 análisis en 6 proyectos y límite 7 no
      // podía abrir el séptimo, aunque le quedaba plaza.
      const { data: activos } = await supabase
        .from('analysis_jobs')
        .select('project_id')
        .eq('status', 'completed')
        .is('archived_at', null)
        .not('file_name', 'ilike', '%demo%');
      const proyectosUsados = new Set(
        (activos ?? []).map((r) => (r as { project_id: string | null }).project_id).filter(Boolean),
      ).size;
      if (proyectosUsados >= org.max_analyses) {
        toast.error(`Has llegado a tu límite de ${org.max_analyses} proyectos activos`, {
          description: 'Archiva un proyecto terminado para liberar plaza, o escríbenos si quieres ampliar el plan.',
        });
        return;
      }
    }
    // Guard de reejecución: si ya existe un análisis COMPLETADO de este mismo guion,
    // confirmar antes de gastar otra pasada. Evita re-análisis accidentales del mismo
    // guion (el principal driver de coste: usuarios re-lanzan 3-5× el mismo largo).
    // NO aplica a versiones (parentJobId): ahí re-subir el mismo nombre es lo normal.
    if (!parentJobId) {
      const { data: existing } = await supabase
        .from('analysis_jobs')
        .select('id')
        .eq('status', 'completed')
        .eq('file_name', selectedFile.name)
        .not('file_name', 'ilike', '%demo%')
        .limit(1);
      if (existing && existing.length > 0) {
        const ok = window.confirm(
          `Ya tienes un análisis de «${selectedFile.name}».\n\nRehacerlo consume una nueva pasada y tarda ~15-20 min. Si solo quieres verlo, cierra esto y ábrelo desde tus análisis.\n\n¿Seguro que quieres volver a analizarlo?`,
        );
        if (!ok) return;
      }
    }
    try {
      const id = await startMutation.mutateAsync({ file: selectedFile, ...opts, parentJobId: parentJobId ?? undefined });
      // Tratamiento (asistente de escritura): no hay análisis de escenas → directo al co-guionista.
      if (opts.sourceDocType === 'tratamiento') {
        navigate(`/narrativa?job=${id}&tab=coguionista`);
        return;
      }
      setJobId(id);
      setView('progress');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      // Tope de análisis de la cuenta (trigger enforce_analysis_limit): mensaje amable.
      if (/l[íi]mite de an[áa]lisis/i.test(message)) {
        const max = message.match(/m[áa]ximo\s+(\d+)/i)?.[1];
        toast.error(
          max ? `Has llegado a tu límite de ${max} análisis` : 'Has llegado a tu límite de análisis',
          { description: 'Tu cuenta tiene un número limitado de análisis. Escríbenos si quieres ampliarlo.' },
        );
      } else {
        toast.error('Error al iniciar: ' + message);
      }
    }
  }

  async function handleCancel() {
    if (!jobId) return;
    try {
      await cancelMutation.mutateAsync(jobId);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      toast.error('Error al cancelar: ' + message);
    }
  }

  function handleNewAnalysis() {
    setView('upload');
    setSelectedFile(null);
    setJobId(null);
    setErrorMessage('');
    navigate('/subir', { replace: true });
  }

  function handleRetry() {
    if (selectedFile) {
      handleStartAnalysis(lastOpts);
    } else {
      setView('upload');
    }
  }

  return (
    <div className="container mx-auto max-w-[1200px] py-12 px-4">
      <div className="mb-6">
        <Link to="/proyectos" className="text-sm text-pelip-text-faint hover:text-pelip-text-soft transition-colors">
          ← Volver a proyectos
        </Link>
      </div>
      {view === 'empty' && (
        <>
          <PageHeader title="Analizar Guion" />
          <EmptyState
            icon={<FileText size={48} />}
            title="Sin guion analizado"
            description="Sube un guion en formato PDF, FDX o Fountain para obtener un desglose completo de escenas, personajes y localizaciones."
            action={<Button onClick={() => setView('upload')}>Subir guion</Button>}
          />
        </>
      )}

      {view === 'upload' && (
        <>
          <PageHeader title={parentJobId ? 'Subir nueva versión' : 'Analizar Guion'} />
          {parentJobId && (
            <Alert className="max-w-2xl mx-auto mb-6">
              <FileText className="h-4 w-4" />
              <AlertTitle>Nueva versión del guion</AlertTitle>
              <AlertDescription>
                Se reutilizará el trabajo de las escenas que no cambien (desglose, ediciones y localizaciones)
                y solo se re-analizará lo nuevo o editado. No consume un análisis de tu plan.
              </AlertDescription>
            </Alert>
          )}
          <ScriptUpload
            selectedFile={selectedFile}
            onFileSelected={setSelectedFile}
            onStartAnalysis={handleStartAnalysis}
            isStarting={startMutation.isPending}
          />
        </>
      )}

      {view === 'progress' && job && (
        <>
          <PageHeader title="Analizar Guion" />
          <AnalysisProgress job={job} onCancel={handleCancel} />
        </>
      )}

      {view === 'results' && jobId && (
        <AnalysisResults jobId={jobId} onNewAnalysis={handleNewAnalysis} />
      )}

      {view === 'error' && (
        <>
          <PageHeader title="Analizar Guion" />
          <Alert variant="destructive" className="max-w-2xl mx-auto">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error en el analisis</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
          <div className="flex gap-3 justify-center mt-6">
            <Button onClick={handleRetry}>Reintentar analisis</Button>
            <Button variant="outline" onClick={handleNewAnalysis}>Subir otro archivo</Button>
          </div>
        </>
      )}
    </div>
  );
}
